import React, { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { formatApiErrorDetail } from "../swof/api";

const inputStyle = {
  width: "100%", padding: "14px 16px", background: "rgba(243,234,215,0.04)",
  border: "1px solid var(--line)", fontFamily: "var(--serif)", fontSize: 16,
  color: "var(--cream)", outline: "none",
};

export function AuthCard({ title, eyebrow, children }) {
  return (
    <div style={{
      minHeight: "100vh", background: "var(--ink)", color: "var(--cream)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{
        width: "100%", maxWidth: 460, border: "1px solid var(--line)",
        padding: "clamp(28px, 4vw, 48px)",
      }}>
        <div className="eyebrow" style={{ marginBottom: 18 }}>{eyebrow}</div>
        <h1 style={{
          fontFamily: "var(--serif)", fontSize: "clamp(32px, 4vw, 44px)",
          margin: "0 0 32px", fontWeight: 400, lineHeight: 1.05, letterSpacing: "-0.02em",
        }}>{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function SubmitButton({ children, busy, testId }) {
  return (
    <button type="submit" disabled={busy} data-testid={testId} style={{
      width: "100%", marginTop: 24, padding: "15px 20px",
      background: busy ? "rgba(201,169,97,0.5)" : "var(--gold)", color: "var(--ink)",
      fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.22em",
      textTransform: "uppercase", border: "none", cursor: busy ? "wait" : "pointer",
    }}>{busy ? "Bezig…" : children}</button>
  );
}

export function ErrorLine({ error }) {
  if (!error) return null;
  return (
    <div data-testid="auth-error" style={{
      marginTop: 18, padding: "12px 14px", border: "1px solid rgba(201,80,60,0.5)",
      background: "rgba(201,80,60,0.08)", fontFamily: "var(--sans)", fontSize: 13,
      color: "#e8b0a4",
    }}>{error}</div>
  );
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthCard eyebrow="Songwriters Wall of Fame" title="Inloggen">
      <form onSubmit={onSubmit}>
        <label style={{ display: "block", marginBottom: 18 }}>
          <span className="eyebrow" style={{ display: "block", marginBottom: 8, fontSize: 10, color: "var(--gold)" }}>E-mail</span>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email" style={inputStyle} autoComplete="username" />
        </label>
        <label style={{ display: "block" }}>
          <span className="eyebrow" style={{ display: "block", marginBottom: 8, fontSize: 10, color: "var(--gold)" }}>Wachtwoord</span>
          <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password" style={inputStyle} autoComplete="current-password" />
        </label>
        <ErrorLine error={error} />
        <SubmitButton busy={busy} testId="login-submit">Inloggen</SubmitButton>
      </form>
      <div style={{ marginTop: 22, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <Link to="/wachtwoord-vergeten" data-testid="login-forgot" style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", color: "var(--gold)", opacity: 0.8,
        }}>Wachtwoord vergeten</Link>
        <Link to="/" style={{
          fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.18em",
          textTransform: "uppercase", opacity: 0.5,
        }}>Naar de site →</Link>
      </div>
    </AuthCard>
  );
}

export { inputStyle };
